import type { FlowFilterCondition, FlowFilterNodeConfig, FlowFilterOp } from '@reflex/flows';
import { PRESENCE_FILTER_OPS } from '@reflex/flows';

/**
 * Prose labels for filter operators in the inspector dropdown and the
 * plain-language recap ("A run starts when …"). Keyed by op so a new
 * `FlowFilterOp` fails to compile until it has a label.
 */
export const FILTER_OP_LABELS: Record<FlowFilterOp, string> = {
  equals: 'equals',
  not_equals: 'not equals',
  contains: 'contains',
  in: 'in',
  intersects: 'intersects',
  exists: 'exists',
  not_exists: 'does not exist',
  truthy: 'is truthy',
};

/**
 * Compact symbol labels for the canvas filter-node card. Distinct from
 * {@link FILTER_OP_LABELS} so the inspector can stay prose while the card
 * stays dense.
 */
export const FILTER_OP_SYMBOLS: Record<FlowFilterOp, string> = {
  equals: '=',
  not_equals: '≠',
  contains: '∋',
  in: 'in',
  intersects: '∩',
  exists: 'exists',
  not_exists: 'does not exist',
  truthy: 'truthy',
};

export interface DescribeFilterOptions {
  /**
   * Optional map from payload field name (without the `trigger.` prefix) to
   * a human title. When present, `trigger.sentryIssueTitle` renders as
   * `issueTitle` / the mapped title rather than the full path.
   */
  fieldTitles?: ReadonlyMap<string, string> | Record<string, string>;
}

/**
 * One boldable segment of the recap sentence. The UI joins them with a
 * leading "A run starts when " and intervening conjunctions.
 */
export interface FilterDescriptionPart {
  /** Field / expression fragment shown in bold. */
  subject: string;
  /** Operator + value (or presence verb) shown in bold after the subject. */
  predicate: string;
}

export interface FilterDescription {
  /** Conjunction between parts: `and` for match=all, `or` for match=any. */
  join: 'and' | 'or';
  parts: FilterDescriptionPart[];
}

function resolveFieldTitle(
  path: string,
  fieldTitles: DescribeFilterOptions['fieldTitles'],
): string {
  const bare = path.startsWith('trigger.') ? path.slice('trigger.'.length) : path;
  if (!fieldTitles) return bare;
  if (fieldTitles instanceof Map) return fieldTitles.get(bare) ?? bare;
  return (fieldTitles as Record<string, string>)[bare] ?? bare;
}

function formatValue(value: FlowFilterCondition['value']): string {
  if (value === undefined) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

function describeCondition(
  condition: FlowFilterCondition,
  fieldTitles: DescribeFilterOptions['fieldTitles'],
): FilterDescriptionPart {
  const subject = resolveFieldTitle(condition.path, fieldTitles);
  const opLabel = FILTER_OP_LABELS[condition.op];
  if (PRESENCE_FILTER_OPS.has(condition.op)) {
    return { subject, predicate: opLabel };
  }
  const value = formatValue(condition.value);
  return { subject, predicate: value ? `${opLabel} ${value}` : opLabel };
}

/**
 * Build a plain-language description of a filter for the inspector recap
 * row ("A run starts when …"). Returns `null` when there is nothing to
 * describe (empty conditions, blank expression) so the row can hide.
 */
export function describeFilter(
  config: FlowFilterNodeConfig,
  options: DescribeFilterOptions = {},
): FilterDescription | null {
  if (config.mode === 'expression') {
    const expression = config.expression.trim();
    if (!expression) return null;
    return {
      join: 'and',
      parts: [{ subject: expression, predicate: '' }],
    };
  }

  const conditions = config.conditions ?? [];
  if (conditions.length === 0) return null;

  return {
    join: config.match === 'any' ? 'or' : 'and',
    parts: conditions.map((c) => describeCondition(c, options.fieldTitles)),
  };
}

/**
 * Render a {@link FilterDescription} as a single plain string for tests
 * and aria labels. The live UI prefers the structured parts so it can
 * bold subjects and predicates.
 */
export function formatFilterDescription(desc: FilterDescription): string {
  const clauses = desc.parts.map((p) => (p.predicate ? `${p.subject} ${p.predicate}` : p.subject));
  return clauses.join(` ${desc.join} `);
}
